import linkifyHtml from 'linkify-html';

import { createErrorMarker, ERROR_CHUNK_MAGIC_CODE } from '@/components/LogViewer/LogErrorMarker';

import { ANSI_STYLES } from './AnsiStyles';
import { StyleBuffer } from './StyleBuffer';

type Range = [number, number];
type ProcessingMode = 'raw' | 'colorized';
type AnsiCodeMatch = { index: number; codes: string; length: number };
type TextChunk = { index: number; range: Range; content: string };
type ProgressResult = { chunkIndex: number; chunkCount: number; value: number };
type ProcessingResult<M extends ProcessingMode = ProcessingMode> = {
  mode: M;
  content: string;
  ansiMatchCount: number;
  activeStyles: StyleBuffer;
};
type ProcessedChunk<M extends ProcessingMode = ProcessingMode> = TextChunk & ProcessingResult<M>;
type ColorizeChunk = (index: number) => ProcessedChunk<'colorized'> | undefined;
type ProcessLogOpts = {
  signal?: AbortSignal;
  chunkSize?: number;
  // Fires once when the raw pass ends; `completed` is false if it was aborted.
  onSettled?: (completed: boolean) => void;
  onProgress?: (progress: ProgressResult) => void;
  // Only raw chunks stream through here; colorized ones are pulled on demand via ColorizeChunk.
  onRawChunk: (chunk: ProcessedChunk<'raw'>) => void;
};

const PROCESSING_DELAY = 0;
const LINES_PER_CHUNK = 1000;
// eslint-disable-next-line no-control-regex
const ANSI_ESCAPE_PATTERN = /\u001b\[(\d*(;\d+)*)?m/g;
const LINKIFY_OPTIONS = {
  target: '_blank',
  className: 'log-viewer__external-link',
  defaultProtocol: 'https',
  nl2br: false,
  rel: 'noopener noreferrer',
} as const;

function sanitize(message: string) {
  return message.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// NOTE: https://en.wikipedia.org/wiki/ANSI_escape_code#8-bit
function ansi256ToRgb(colorIndex: number, field: 'color' | 'background-color'): Record<string, string> {
  // standard colors as in ESC[30–37m and ESC[40–47m ANSI escape sequences
  if (colorIndex < 8) {
    const index = colorIndex + (field === 'color' ? 30 : 40);
    return ANSI_STYLES[index];
  }
  // high intensity colors as in ESC[90–97m and ESC[100–107m ANSI escape sequences
  if (colorIndex < 16) {
    const index = colorIndex + (field === 'color' ? 82 : 92);
    return ANSI_STYLES[index];
  }
  // 6x6x6 color cube (16-231)
  if (colorIndex < 232) {
    const index = colorIndex - 16;
    const r = Math.floor(index / 36) * 51;
    const g = Math.floor((index % 36) / 6) * 51;
    const b = (index % 6) * 51;
    return { [field]: `rgb(${r}, ${g}, ${b})` };
  }
  // Grayscale (232-255) in 24 steps
  const gray = (colorIndex - 232) * 10 + 8;
  return { [field]: `rgb(${gray}, ${gray}, ${gray})` };
}

function getAnsiCodeMatches(text: string): Array<AnsiCodeMatch> {
  const matches: Array<AnsiCodeMatch> = [];
  let match;

  // Collect all ANSI escape sequence positions and codes
  while ((match = ANSI_ESCAPE_PATTERN.exec(text)) !== null) {
    matches.push({
      index: match.index,
      codes: match[1] ?? '0',
      length: match[0].length,
    });
  }

  return matches;
}

function processAnsiCodes(sequence: string, activeStyles: StyleBuffer): StyleBuffer {
  let i = 0;
  const newStyleBuffer = new StyleBuffer(activeStyles);

  const codes = sequence.split(';');

  while (i < codes.length) {
    const code = codes[i];

    if (code === '0') {
      // Reset
      newStyleBuffer.clear();
      i += 1;
    } else if (
      code === '21' || // Reset bold
      code === '22' || // Reset dim
      code === '23' || // Reset italic
      code === '24' || // Reset underline
      code === '28' || // Reset hidden
      code === '29' || // Reset strikethrough
      code === '39' || // Reset color
      code === '49' // Reset background-color
    ) {
      // Reset specific styles
      const style = ANSI_STYLES[parseInt(code, 10)];
      if (style) {
        newStyleBuffer.delete(...Object.keys(style));
      }

      i += 1;
    } else if ((code === '38' || code === '48') && codes[i + 1] === '5') {
      // 256 color
      let colorIndex = parseInt(codes[i + 2], 10);
      colorIndex = Number.isNaN(colorIndex) ? 0 : colorIndex;
      newStyleBuffer.merge(ansi256ToRgb(colorIndex, code === '38' ? 'color' : 'background-color'));
      i += 3;
    } else if ((code === '38' || code === '48') && codes[i + 1] === '2') {
      // 24bit RGB color
      const r = codes[i + 2] || 0;
      const g = codes[i + 3] || 0;
      const b = codes[i + 4] || 0;
      newStyleBuffer.merge({ [code === '38' ? 'color' : 'background-color']: `rgb(${r}, ${g}, ${b})` });
      i += 5;
    } else {
      const style = ANSI_STYLES[parseInt(code, 10)];
      if (style) {
        newStyleBuffer.merge(style);
      }
      i += 1;
    }
  }

  return newStyleBuffer;
}

function processMatches(
  text: string,
  buffer: string[],
  matches: Array<AnsiCodeMatch>,
  process?: (match: AnsiCodeMatch) => void,
): void {
  let lastIndex = 0;

  // Reconstruct text with processing the matches
  matches.forEach((match) => {
    if (match.index >= lastIndex) {
      buffer.push(text.slice(lastIndex, match.index));

      if (process) {
        process(match);
      }

      // Create error marker for error marker magic sequence
      if (match.codes.includes(ERROR_CHUNK_MAGIC_CODE.toString())) {
        buffer.push(createErrorMarker());
      }
    }
    lastIndex = match.index + match.length;
  });

  // Add remaining text after last match
  if (lastIndex < text.length) {
    buffer.push(text.slice(lastIndex));
  }
}

/**
 * Efficiently remove ANSI escape sequences from text
 * Optimized to minimize string allocations (no regex replace)
 */
function stripAnsi(text: string): ProcessingResult<'raw'> {
  const matches = getAnsiCodeMatches(text);

  // If no matches, return original text
  if (matches.length === 0) {
    return { mode: 'raw', content: text, ansiMatchCount: 0, activeStyles: new StyleBuffer() };
  }

  const buffer: string[] = [];
  processMatches(text, buffer, matches);

  return {
    mode: 'raw',
    content: buffer.join(''),
    ansiMatchCount: matches.length,
    activeStyles: new StyleBuffer(),
  };
}

/**
 * Convert ANSI color/style codes to HTML spans
 * Supports standard colors, 256-color and 24-bit RGB colors, text decoration and multi-code escape sequences, and nested styles
 */
function convertAnsiToHtml(
  text: string,
  initialStyles: StyleBuffer = new StyleBuffer(),
): ProcessingResult<'colorized'> {
  const buffer: string[] = [];

  let spanCount = 0;
  let activeStyles = new StyleBuffer(initialStyles);

  if (activeStyles.length > 0) {
    buffer.push(`<span style="${activeStyles}">`);
    spanCount += 1;
  }

  const matches = getAnsiCodeMatches(text);

  processMatches(text, buffer, matches, (match) => {
    // Process the ANSI codes to determine new styles
    const newStyles = processAnsiCodes(match.codes, activeStyles);

    if (activeStyles.toString() !== newStyles.toString()) {
      if (spanCount > 0) {
        buffer.push('</span>'.repeat(spanCount));
        spanCount = 0;
      }

      activeStyles = newStyles;

      if (newStyles.length > 0) {
        buffer.push(`<span style="${newStyles}">`);
        spanCount += 1;
      }
    }
  });

  // Close all spans
  if (spanCount > 0) {
    buffer.push('</span>'.repeat(spanCount));
  }

  return {
    mode: 'colorized',
    content: buffer.join(''),
    ansiMatchCount: matches.length,
    activeStyles,
  };
}

function chunkLog(text: string, chunkSize = LINES_PER_CHUNK): TextChunk[] {
  const rawLines = text.split('\n');
  const lineCount = rawLines.length;
  const chunkCount = Math.ceil(lineCount / chunkSize);

  // Pre-allocate chunks array
  const textChunks = new Array<TextChunk>(chunkCount);
  for (let index = 0; index < chunkCount; index++) {
    const start = index * chunkSize;
    const end = Math.min(start + chunkSize, lineCount);
    textChunks[index] = {
      index,
      range: [start, end] as Range,
      content: rawLines.slice(start, end).join('\n'),
    };
  }
  // Free up rawLines array
  rawLines.length = 0;

  return textChunks;
}

function processRawChunk(chunk: TextChunk): ProcessedChunk<'raw'> {
  return {
    ...chunk,
    ...stripAnsi(sanitize(chunk.content)),
  };
}

function processColorizedChunk(chunk: TextChunk, activeStyles: StyleBuffer): ProcessedChunk<'colorized'> {
  const result = convertAnsiToHtml(sanitize(chunk.content), activeStyles);
  const linkifiedContent = linkifyHtml(result.content, LINKIFY_OPTIONS);

  return {
    ...chunk,
    ...result,
    content: linkifiedContent,
  };
}

/**
 * Each chunk's entering ANSI style, precomputed so a chunk can be colorized alone, in any order.
 * A chunk can inherit a sequence opened arbitrarily far back, so there is no scan-back shortcut —
 * but folding state (no HTML) is ~60ms/500k lines vs ~4s to emit, which is what lets colorization
 * be deferred to on-screen chunks. Runs on unsanitized text: sanitize() only rewrites <>, so it
 * can neither add nor remove an escape sequence, and skipping it avoids a second pass.
 */
function computeStyleBoundaries(chunks: TextChunk[]): StyleBuffer[] {
  const boundaries = new Array<StyleBuffer>(chunks.length);
  let activeStyles = new StyleBuffer();

  for (let index = 0; index < chunks.length; index++) {
    boundaries[index] = activeStyles;

    getAnsiCodeMatches(chunks[index].content).forEach((match) => {
      activeStyles = processAnsiCodes(match.codes, activeStyles);
    });
  }

  return boundaries;
}

async function processRawChunks(
  chunks: TextChunk[],
  { signal, onProgress, onRawChunk }: ProcessLogOpts,
): Promise<void> {
  const chunkCount = chunks.length;

  return new Promise<void>((resolve) => {
    let chunkIndex = 0;

    const processNextChunk = () => {
      if (signal?.aborted) {
        resolve();
        return;
      }

      onRawChunk(processRawChunk(chunks[chunkIndex]));
      onProgress?.({
        chunkIndex: chunkIndex + 1,
        chunkCount,
        value: Math.floor(((chunkIndex + 1) / chunkCount) * 100),
      });
      chunkIndex += 1;

      if (chunkIndex < chunkCount) {
        setTimeout(processNextChunk, PROCESSING_DELAY);
      } else {
        resolve();
      }
    };

    setTimeout(() => processNextChunk(), PROCESSING_DELAY);
  });
}

/**
 * Streams the raw chunks (cheap, up front) and returns `colorize`, which produces a chunk's
 * colorized HTML on demand -- the expensive half, run only for chunks that scroll into view.
 * Completion is reported through `onSettled`, so the caller never has to await anything.
 */
function processLog(text: string, opts: ProcessLogOpts): ColorizeChunk {
  const chunks = chunkLog(text, opts?.chunkSize);
  const styleBoundaries = computeStyleBoundaries(chunks);

  const colorize: ColorizeChunk = (index) => {
    const chunk = chunks[index];
    return chunk ? processColorizedChunk(chunk, styleBoundaries[index]) : undefined;
  };

  processRawChunks(chunks, opts).then(() => {
    opts?.onSettled?.(!opts?.signal?.aborted);
  });

  return colorize;
}

export {
  ansi256ToRgb,
  chunkLog,
  type ColorizeChunk,
  convertAnsiToHtml,
  processAnsiCodes,
  type ProcessedChunk,
  processLog,
  type ProgressResult,
  type Range,
  sanitize,
  stripAnsi,
};
